// Pure logic for onboarding a client from the ops console: turning probe
// results into editable crawl sources, and the editor's state back into the
// crawl config and provision payload the API takes (app/services/crawl_config.py).

export const DOMAINS = [
  { id: 'general', label: 'General (town, county, nonprofit)', help: 'No pension-specific guards.' },
  { id: 'transit', label: 'Transit agency', help: 'Adds trip planning from the agency GTFS feed.' },
  { id: 'pension', label: 'Pension / retirement system', help: 'Advice and look-alike-agency guards on.' },
];

export const MODES = [
  { id: 'wp', label: 'WordPress API', help: 'Pages, posts and the PDF library from the REST API, gap-filled from the sitemap.' },
  { id: 'sitemap', label: 'Sitemap', help: 'Exactly the URLs the sitemap lists.' },
  { id: 'crawl', label: 'Follow links', help: 'Pages found by following links from the home page.' },
  { id: 'pages', label: 'Listed pages', help: 'Exactly the pages listed, one URL per line: a few pages of another organization\'s site, such as the county\'s elections pages.' },
];

/** Same rule as the server's _slugify (ops.py): lowercase words joined by '-', 40 chars max. */
export function slugify(value) {
  const trim = (s) => s.replace(/^-+|-+$/g, '');
  return trim(trim((value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')).slice(0, 40));
}

/** URLs typed one per line (or comma-separated), trimmed, de-duplicated, in order. */
export function parseUrlList(text) {
  const seen = new Set();
  return (text || '').split(/[\n,]/).map((s) => s.trim()).filter((s) => {
    if (!s || seen.has(s.toLowerCase())) return false;
    seen.add(s.toLowerCase());
    return true;
  });
}

/** Exclude entries (globs or {urlPattern, uploadedBefore}) as editor rows. */
export function excludeRows(entries) {
  return (entries || []).map((e) => (typeof e === 'string'
    ? { kind: 'glob', pattern: e, before: '' }
    : { kind: 'rule', pattern: e.urlPattern || '', before: e.uploadedBefore || '' }));
}

/** Editor rows back to exclude entries; blank rows are dropped. */
export function excludeEntries(rows) {
  return (rows || []).filter((r) => (r.pattern || '').trim()).map((r) => {
    if (r.kind === 'glob') return r.pattern.trim();
    return r.before ? { urlPattern: r.pattern.trim(), uploadedBefore: r.before } : { urlPattern: r.pattern.trim() };
  });
}

/** One editable source row from a probe result. Unproposed sites start unchecked. */
export function sourceFromProbe(result) {
  const p = result.proposed || {};
  return {
    key: result.site || result.input,
    include: !!result.proposed,
    site: p.site || result.site || '',
    mode: p.mode || 'crawl',
    sitemapUrl: p.sitemapUrl || (result.sitemap && result.sitemap.url) || '',
    linkedPdfs: !!p.linkedPdfs,
    pagesText: '',
    publisher: '',
    excludes: [],
    probe: result,
  };
}

/** Editable rows from a stored crawl config (sources form). Single-site stored
 *  configs (frontBase form) aren't representable as rows: callers edit them as JSON. */
export function sourcesFromCrawl(crawl) {
  if (!crawl || !Array.isArray(crawl.sources)) return [];
  return crawl.sources.map((s) => ({
    key: s.site,
    include: true,
    site: s.site,
    mode: s.mode || 'wp',
    sitemapUrl: s.sitemapUrl || '',
    linkedPdfs: !!s.linkedPdfs,
    pagesText: (s.pages || []).join('\n'),
    publisher: s.publisher || '',
    excludes: excludeRows(s.exclude),
    extra: Object.fromEntries(Object.entries(s).filter(([k]) =>
      !['site', 'mode', 'sitemapUrl', 'linkedPdfs', 'exclude', 'pages', 'publisher'].includes(k))),
    probe: null,
  }));
}

/** The crawl config for the included rows (the shape PUT /api/corpus/crawl takes).
 *  `exclude` is the agency-level list (applies to every site); it and the
 *  other top-level settings pass through untouched when editing a stored config. */
export function buildCrawlConfig(rows, { sectionRecords = true, notes = '', exclude = [] } = {}) {
  const sources = rows.filter((r) => r.include && r.site).map((r) => {
    const src = { ...(r.extra || {}), site: r.site, mode: r.mode };
    // wp mode gap-fills from the sitemap (a non-default one must survive a
    // save); sitemap mode reads exactly it; link crawl and listed pages don't use one.
    if (r.mode !== 'crawl' && r.mode !== 'pages' && r.sitemapUrl) src.sitemapUrl = r.sitemapUrl;
    if ((r.mode === 'sitemap' || r.mode === 'pages') && r.linkedPdfs) src.linkedPdfs = true;
    if (r.mode === 'pages') src.pages = parseUrlList(r.pagesText);
    if ((r.publisher || '').trim()) src.publisher = r.publisher.trim();
    const exclude = excludeEntries(r.excludes);
    if (exclude.length) src.exclude = exclude;
    return src;
  });
  const cfg = { sources, sectionRecords };
  if (exclude && exclude.length) cfg.exclude = exclude;
  if (notes && notes.trim()) cfg.notes = notes.trim();
  return cfg;
}

/** Problems that would make the API refuse the config, said before it does. */
export function sourceProblems(rows) {
  const problems = [];
  const included = rows.filter((r) => r.include);
  if (!included.length) problems.push('Include at least one site.');
  const seen = new Map();
  included.forEach((r) => {
    let host = '';
    try { host = new URL(r.site).hostname.replace(/^www\./, ''); } catch { problems.push(`Not a site URL: ${r.site || '(empty)'}`); return; }
    if (seen.has(host)) problems.push(`${r.site} repeats ${seen.get(host)}.`);
    seen.set(host, r.site);
    if (r.mode === 'sitemap' && !r.sitemapUrl) problems.push(`${r.site}: sitemap mode needs the sitemap URL.`);
    if (r.mode === 'pages' && !parseUrlList(r.pagesText).length) problems.push(`${r.site}: list at least one page.`);
    (r.excludes || []).forEach((x) => {
      const pat = (x.pattern || '').trim();
      if (x.kind === 'glob' && pat && !pat.startsWith('/') && !/^https?:\/\//i.test(pat)) {
        problems.push(`${r.site}: path exclude "${pat}" must start with /`);
      }
    });
  });
  return problems;
}

export function defaultBot(name, phones = []) {
  return {
    display_name: name ? `${name} Assistant` : '',
    starter_questions: ['', '', '', ''],
    contact_text: phones[0] || '',
    refusal_message: '',
  };
}

/** widget/persona fragments for the bot settings the onboarding flow edits. */
export function botSettings(bot) {
  const widget = { starter_questions: (bot.starter_questions || []).map((q) => q.trim()).filter(Boolean) };
  if (bot.display_name && bot.display_name.trim()) widget.display_name = bot.display_name.trim();
  const persona = {};
  if (bot.contact_text && bot.contact_text.trim()) persona.contact_text = bot.contact_text.trim();
  if (bot.refusal_message && bot.refusal_message.trim()) persona.refusal_message = bot.refusal_message.trim();
  return { widget, persona };
}

/** POST /api/admin/ops/provision body for a new demo. */
export function provisionPayload({ name, slug, domain, channel, rows, bot }) {
  const { widget, persona } = botSettings(bot);
  return {
    mode: 'demo',
    name: name.trim(),
    slug: slugify(slug || name),
    site_url: channel,
    domain,
    crawl: buildCrawlConfig(rows),
    widget,
    persona,
  };
}

/** Stored local terms ([{term, aliases}], app/services/local_terms.py) as
 *  editable text: one term per line, "Board of Commissioners = city council, town council". */
export function termsText(terms) {
  return (terms || []).map((t) => `${t.term} = ${(t.aliases || []).join(', ')}`).join('\n');
}

/** Stored staff answers (app/services/staff_answers.py) as editor rows. */
export function staffRows(answers) {
  return (answers || []).map((a) => ({
    id: a.id || '',
    questionsText: (a.questions || []).join('\n'),
    answer: a.answer || '',
    link: a.link || '',
    approvedOn: a.approved_on || '',
  }));
}

/** Editor rows back to the list the API takes. Blank rows are dropped; one
 *  question per line; approved_on only when a date is set. The server
 *  validates the rest and names the row in its errors. */
export function staffPayload(rows) {
  return (rows || []).filter((r) => [r.id, r.questionsText, r.answer, r.link].some((v) => (v || '').trim()))
    .map((r) => {
      const out = {
        id: (r.id || '').trim(),
        questions: (r.questionsText || '').split('\n').map((q) => q.trim()).filter(Boolean),
        answer: (r.answer || '').trim(),
        link: (r.link || '').trim(),
      };
      if (r.approvedOn) out.approved_on = r.approvedOn;
      return out;
    });
}

/** termsText back to the list the API takes. A line without "=" is sent
 *  with no aliases, so the server names it in its errors instead of the
 *  line silently disappearing. */
export function parseTerms(text) {
  return (text || '').split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
    const at = line.indexOf('=');
    if (at < 0) return { term: line, aliases: [] };
    return {
      term: line.slice(0, at).trim(),
      aliases: line.slice(at + 1).split(',').map((a) => a.trim()).filter(Boolean),
    };
  });
}
